import { useState, useRef, useEffect, useCallback } from "react";
import { Table, Empty } from "antd";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import useAuth from "../../hooks/useAuth";
const type = "DraggableColumn";
const DEFAULT_FIXED_COUNT = 2;
const MIN_COLUMN_WIDTH = 30; 
const DEFAULT_COLUMN_WIDTH = 120; 
const COLUMN_WIDTH_CONFIG = {
  status: 30,
  crewName: 200,
  eventName: 200,
  employeesNeeded: 100,
  employeesScheduled: 115,
  difference: 80,
  noShow: 105,
  startTime: 80,
  endTime: 80,
  ssn4: 85,
  start: 80,
  end: 80,
  id: 80,
  ssn4: 60,
  union: 80,
  name:100,
  unionStat: 100,
  trade: 100,
  schedStat: 100,
  memberId: 80,
  phoneNo: 80,
  onPremises: 80,
  currentCrewLead: 100,
  reference:80,
  nextScheduled: 80,
  currentCrew: 100,
  owner: 90,
  crewLead: 100,
  multischeduled: 70,
  start: 70,
  end: 70,
  event: 100,
  eventDescription: 100,
  task: 70,
  venue: 80,
  payScale: 70,
  payscaleDescription: 70,
  premium: 70,
  workRuleTransfer: 100,
  supervisor: 80,
  assignedAt: 60,
  scheduledBy: 80,
  systemMessaged: 80,
  dispatched: 70,
};
const DraggableHeaderCell = ({
  index,
  moveColumn,
  onResize,
  width,
  children,
  ...restProps
}) => {
  const ref = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [, drop] = useDrop({
    accept: type,
    drop: (item) => {
      if (item.index !== index) {
        moveColumn(item.index, index);
        item.index = index;
      }
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    canDrag: () => !isResizing,
  });
  drag(drop(ref)); 
  if (!width) {
    return (
      <th ref={ref} {...restProps}>
        {children}
      </th>
    );
  }
   return (
    <Resizable
      width={width}
      height={0}
      onResizeStart={() => setIsResizing(true)}
      onResize={onResize}
      onResizeStop={() => setTimeout(() => setIsResizing(false), 50)}
      draggableOpts={{ enableUserSelectHack: false }}
      minConstraints={[MIN_COLUMN_WIDTH, 0]}
      handle={
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: "8px",
            cursor: "col-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 2,
              height: "40%",
              backgroundColor: "#cfcfcf",
              borderRadius: 1,
            }}
          />
        </div>
      }
    >
      <th
        ref={ref}
        {...restProps}
        style={{
          cursor: isResizing ? "col-resize" : "move",
          userSelect: "none",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...restProps.style,
          backgroundColor: "#EAEEFF",
        }}
      >
        {children}
      </th>
    </Resizable>
  );
};
const SmartTable = ({
  tableKey,
  columns: inputColumns = [],
  dataSource = [],
  rowSelection,
  rowKey,
  tableLayout: tableLayoutProp = "fixed",
  onFilteredDataChange,
  disableFilterPersistence,
  ...tableProps
}) => {
  const { userConfig } = useAuth();
  const userId = userConfig?.userid;
  const { onChange: parentOnChange, ...restTableProps } = tableProps;
const sanitizeFilters = (filters) => {
    if (!filters) return {};
    const cleaned = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (!Array.isArray(value)) return;
      const nonEmpty = value.filter((v) => v !== null && v !== undefined && v !== '');
      if (nonEmpty.length) cleaned[key] = nonEmpty;
    });
    return cleaned;
  };
  const resolvedTableLayout = tableLayoutProp ?? "fixed";
  const isEmpty = !dataSource || dataSource.length === 0;
  const resolvedDataSource = isEmpty ? [] : dataSource;
  if (!tableKey) {
    tableKey = "table_" + inputColumns.map((c) => c.key).join("_");
  }
   const storageKey = `smartLayout_${userId}_${tableKey}`;
 const ensureDefaultFixed = (cols) => {
    const fixedCount = cols.filter((c) => c.fixed).length;
    if (fixedCount === 0) {
      for (let i = 0; i < DEFAULT_FIXED_COUNT && i < cols.length; i++) {
        cols[i].fixed = "left";
      }
    }
    return cols;
  };
const loadLayout = (cols) => {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return ensureDefaultFixed(cols);
   try {
      const parsed = JSON.parse(saved);
      const rawOrder = Array.isArray(parsed.order) ? parsed.order : [];
      const uniqueOrder = Array.from(new Set(rawOrder));
      const ordered = uniqueOrder
        .map((k) => cols.find((c) => c.key === k))
        .filter(Boolean);
      const missing = cols.filter((c) => !uniqueOrder.includes(c.key));
      const merged = [...ordered, ...missing];
      const seen = new Set();
      const uniqueMerged = merged.filter((c) => {
        if (seen.has(c.key)) return false;
        seen.add(c.key);
        return true;
      });
        return ensureDefaultFixed(
        uniqueMerged.map((c) => {
          const savedWidth = parsed.widths?.[c.key];
          const defaultWidth = COLUMN_WIDTH_CONFIG[c.dataIndex];
          if (defaultWidth && !savedWidth) {
            return { ...c, width: defaultWidth, fixed: parsed.fixed?.[c.key] ?? c.fixed };
          }
          return {
            ...c,
            width: Math.max(savedWidth ?? c.width ?? DEFAULT_COLUMN_WIDTH, MIN_COLUMN_WIDTH),
            fixed: parsed.fixed?.[c.key] ?? c.fixed,
          };
        })
      );
    } catch {
      return ensureDefaultFixed(cols);
    }
  };
  const [columns, setColumns] = useState(() =>
    loadLayout(
      inputColumns.map((c) => ({
        ...c,
        width: COLUMN_WIDTH_CONFIG[c.dataIndex] || Math.max(c.width || DEFAULT_COLUMN_WIDTH, MIN_COLUMN_WIDTH),
      }))
    )
  );
const saveLayout = (cols) => {
    const order = cols.map((c) => c.key);
    const uniqueOrder = Array.from(new Set(order));
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        order: uniqueOrder,
        widths: Object.fromEntries(cols.map((c) => [c.key, c.width])),
        fixed: Object.fromEntries(cols.map((c) => [c.key, c.fixed])),
      })
    );
  };
useEffect(() => {
    setColumns(
      loadLayout(
        inputColumns.map((c) => ({
          ...c,
          width: COLUMN_WIDTH_CONFIG[c.dataIndex] || Math.max(c.width || DEFAULT_COLUMN_WIDTH, MIN_COLUMN_WIDTH),
          fixed: c.fixed,
        }))
      )
    );
  }, [inputColumns, userId]);
const moveColumn = (from, to) => {
    setColumns((prev) => {
      const next = [...prev];
      const [dragged] = next.splice(from, 1);
      next.splice(to, 0, dragged);
      const left = next[to - 1];
      const right = next[to + 1];
      const neighborIsFixed = (left && left.fixed) || (right && right.fixed);
      if (to === 0 || neighborIsFixed) {
        dragged.fixed = "left";
      } else {
        dragged.fixed = undefined;
      }
      ensureDefaultFixed(next);
      saveLayout(next);
      return next;
    });
  };
const handleResize =
    (index) =>
    (_, { size }) => {
      setColumns((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], width: Math.max(size.width, MIN_COLUMN_WIDTH) };
        saveLayout(next);
        return next;
      });
    };
  const filterStorageKey = `smartFilters_${userId}_${tableKey}`;
 const loadFilters = () => {
    try {
      const saved = localStorage.getItem(filterStorageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (err) {
      console.error('Failed to load filters from localStorage', err);
    }
    return {};
  };
  const [activeFilters, setActiveFilters] = useState(disableFilterPersistence ? {} : loadFilters);
  const saveFilters = (filters) => {
    if (disableFilterPersistence) return;
    try {
      localStorage.setItem(filterStorageKey, JSON.stringify(filters));
    } catch (err) {
      console.error('Failed to save filters to localStorage', err);
    }
  };
  const mergedColumns = columns.map((col, index) => {
    const originalFilterDropdown = col.filterDropdown;
    const wrappedFilterDropdown = originalFilterDropdown
      ? (props) => originalFilterDropdown({ ...props, tableFilters: activeFilters })
      : undefined;
    const filteredValue = activeFilters[col.key] || activeFilters[col.dataIndex] || null;
    return {
      ...col,
      ellipsis: true,
      filterDropdown: wrappedFilterDropdown,
      filteredValue,
      onHeaderCell: (record) => {
        const originalProps = col.onHeaderCell ? col.onHeaderCell(record) : {};
        return {
          ...originalProps,
          index,
          width: col.width,
          moveColumn,
          onResize: handleResize(index),
        };
      },
    };
  });
  const [filteredData, setFilteredData] = useState(resolvedDataSource);
  useEffect(() => {
    const filtered = applyFiltersToData(resolvedDataSource, activeFilters);
    setFilteredData(filtered);
    if (typeof onFilteredDataChange === 'function') {
      onFilteredDataChange(filtered);
    }
  }, [JSON.stringify(resolvedDataSource), JSON.stringify(activeFilters)]);
const getRecordKey = (record) => {
    if (typeof rowKey === 'function') return rowKey(record);
    if (typeof rowKey === 'string') return record[rowKey];
    return record.key ?? record.id;
  };
const applyFiltersToData = (data, activeFilters) => {
    if (!activeFilters || Object.keys(activeFilters).length === 0) return data;
    const selectedKeys = new Set(rowSelection?.selectedRowKeys || []);
    return data.filter((record) => {
      const recordKey = getRecordKey(record);
      if (selectedKeys.has(recordKey)) return true;
      for (const [colKey, values] of Object.entries(activeFilters)) {
        if (!values || values.length === 0) continue; 
       const col = mergedColumns.find((c) => c.key === colKey || c.dataIndex === colKey);
        const matchesAny = values.some((val) => {
          try {
            if (col && typeof col.onFilter === 'function') {
              return col.onFilter(val, record);
            }
            const recVal = String(record[colKey] ?? '').toLowerCase();
            return recVal.includes(String(val).toLowerCase());
          } catch (e) {
            return false;
          }
        });
      if (!matchesAny) return false; 
      }
      return true; 
    });
  };
const handleTableChange = (pagination, filters, sorter, extra) => {
    const cleanedFilters = sanitizeFilters(filters);
    setActiveFilters(cleanedFilters);
    saveFilters(cleanedFilters);
    const next = applyFiltersToData(resolvedDataSource, cleanedFilters);
    setFilteredData(next);
    if (typeof parentOnChange === 'function') {
      parentOnChange(pagination, cleanedFilters, sorter, extra);
    }
  };

  const clearAllFilters = useCallback(() => {
    setActiveFilters({});
    saveFilters({});
    setFilteredData(resolvedDataSource);
  }, [resolvedDataSource, saveFilters]);

  useEffect(() => {
    if (tableProps.onFiltersReady) {
      tableProps.onFiltersReady({ clearAllFilters });
    }
  }, [tableProps.onFiltersReady, clearAllFilters]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div style={{ width: "100%" }}>
        <Table
          bordered
          components={{ header: { cell: DraggableHeaderCell } }}
          columns={mergedColumns}
          dataSource={filteredData}
          rowSelection={rowSelection}
          rowKey={rowKey}
          pagination={false}
          tableLayout={resolvedTableLayout}
          locale={{ emptyText: isEmpty ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Data" /> : undefined }}
          scroll={{ x: 'max-content' }}
          {...restTableProps}
          onChange={handleTableChange}
          style={{ minWidth: "100%", ...restTableProps.style }}
        />
      </div>
    </DndProvider>
  );
};

export default SmartTable;